import { useQuery } from "@tanstack/react-query";
import { Flow, type PlatformMeta } from "../api/generated/model";
import { fetchSkuCounts } from "../api/skuCounts";
import { platformLabel, platforms } from "../api/platforms";
import { fmtInt } from "../api/labels";
import { AnimatedNumber } from "../lib/motion";
import { Badge, EmptyState, ErrorState, PageHeader, Panel, Row, Skeleton, SkeletonRegion, Stack } from "@akaike/ui";
import { TIER_CHIP } from "../components/tiers";
import { Stat } from "../components/foundation";

// The adapter-backed flows shown as capability pills, in display order. taxonomy_classify
// is platform-agnostic (not a per-platform capability) and deliberately absent.
const CAP_FLOWS: { key: Flow; label: string }[] = [
  { key: Flow.cart_inventory, label: "inventory" },
  { key: Flow.brand_sweep, label: "brand" },
  { key: Flow.merchant_sweep, label: "merchant" },
  { key: Flow.store_sku_map, label: "store map" },
];

// The Platforms roster - the pluggable per-platform adapters behind the shared flow
// engines, rendered straight from the registry contract (GET /v1/platforms). Adding a
// platform makes a new card appear here automatically once its SPEC is registered.
export function Platforms() {
  const specs = platforms();

  // Per-platform active SKU totals, same source as Brands.
  const countsQuery = useQuery({
    // Platform totals count every active SKU, combos included; keyed on the combos flag so
    // it shares cache with other pages requesting the combos-included counts.
    queryKey: ["sku-counts", true],
    queryFn: () => fetchSkuCounts(true),
    refetchInterval: 30000,
  });
  // The dimension footprint, not the scrape universe: this page is about what each platform
  // HAS, and it has no brand-toggle context to net operator switches out against. The
  // Brands page owns the "what will actually scrape" number.
  const skuTotal = (key: string) =>
    Object.values(countsQuery.data?.[key] ?? {}).reduce((a, c) => a + c.total, 0);

  const flowsSupported = specs.reduce(
    (a, p) => a + CAP_FLOWS.filter((f) => p.capabilities[f.key]).length,
    0,
  );
  const totalSkus = specs.reduce((a, p) => a + skuTotal(p.key), 0);
  const wafGated = specs.filter((p) => p.needs_waf).length;

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Platforms"
        description="Every q-commerce source is a pluggable adapter - serviceability, cart, and sweep parsers behind one engine. Adding a platform never touches the flows."
      />

      <div className="grid g-4 mb-3.5">
        <Stat
          surface
          label="platforms"
          value={<AnimatedNumber value={specs.length} />}
          tone="ok"
          foot={<span className="faint">registered adapters</span>}
        />
        <Stat
          surface
          label="flows supported"
          value={<AnimatedNumber value={flowsSupported} />}
          unit={`/ ${specs.length * CAP_FLOWS.length}`}
          tone="ok"
          foot={<span className="faint">across platforms</span>}
        />
        <Stat
          surface
          label="active SKUs"
          value={<AnimatedNumber value={totalSkus} format={fmtInt} />}
          foot={<span className="faint">current dimension</span>}
        />
        <Stat
          surface
          label="anti-bot gated"
          value={<AnimatedNumber value={wafGated} />}
          tone="warn"
          foot={<span className="faint">WAF token mint</span>}
        />
      </div>

      {countsQuery.isLoading && !countsQuery.data ? (
        <SkeletonRegion label="Loading platforms">
          <Stack gap="sm" className="p-4">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} h={28} />
            ))}
          </Stack>
        </SkeletonRegion>
      ) : countsQuery.isError ? (
        <ErrorState
          compact
          title="Could not load SKU counts"
          message="Is the control plane up?"
          onRetry={() => countsQuery.refetch()}
        />
      ) : specs.length === 0 ? (
        <EmptyState compact title="No platforms registered" />
      ) : (
        <div className="plat-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(322px,1fr))" }}>
          {specs.map((p) => (
            <PlatformCard key={p.key} p={p} skus={skuTotal(p.key)} />
          ))}
        </div>
      )}

      <Panel className="mt-3.5">
        <Panel.Head title="What an integration ships">
          <div className="flex-1" />
          <span className="faint num text-[11px]">qcs.core.platforms.&lt;name&gt;</span>
        </Panel.Head>
        <Panel.Body>
          <div className="grid g-4">
            <Anatomy file="serviceability.py" desc="probe (lat,lon) -> store ids · feeds merchant sweep" />
            <Anatomy file="cart.py" desc="cart payload + parse · feeds cart inventory" />
            <Anatomy file="sweep.py" desc="search / PDP parse · feeds brand sweep" />
            <Anatomy file="spec.py" desc="the PlatformSpec - one declaration wires it all" />
          </div>
        </Panel.Body>
      </Panel>
    </>
  );
}

function PlatformCard({ p, skus }: { p: PlatformMeta; skus: number }) {
  return (
    <Panel className="p-0 overflow-hidden">
      <Panel.Head>
        <span className={`plat ${p.key}`} style={{ fontSize: 13 }}>
          {platformLabel(p.key)}
        </span>
        <div className="flex-1" />
        {p.needs_waf && <Badge variant="outline-warning">WAF</Badge>}
      </Panel.Head>
      <Panel.Body>
        <Stack gap="md">
          <Stack gap="sm">
            <span className="label">flows</span>
            <Row gap="xs" wrap>
              {CAP_FLOWS.map((f) => {
                const on = !!p.capabilities[f.key];
                return (
                  <Badge key={f.key} variant={on ? "outline-success" : "outline"}>
                    {f.label}
                    {on ? "" : " -"}
                  </Badge>
                );
              })}
            </Row>
          </Stack>
          <Stack gap="sm">
            <span className="label">{p.has_lanes ? "delivery lanes" : "tiers"}</span>
            <Row gap="xs" wrap>
              {p.tiers.map((t) => (
                <Badge
                  key={t.token}
                  variant="outline"
                  className={TIER_CHIP}
                  style={{
                    color: t.color,
                    borderColor: t.color,
                    background: `${t.color}1f`,
                  }}
                >
                  {t.label}
                </Badge>
              ))}
            </Row>
          </Stack>
          <div className="kv">
            <dt>active SKUs</dt>
            <dd>{fmtInt(skus)}</dd>
            <dt>anti-bot</dt>
            <dd>{p.needs_waf ? "WAF token mint" : "none"}</dd>
            <dt>PDP link</dt>
            <dd>{p.pdp_url_template ? "yes" : "-"}</dd>
          </div>
        </Stack>
      </Panel.Body>
    </Panel>
  );
}

function Anatomy({ file, desc }: { file: string; desc: string }) {
  return (
    <Stack gap="xs">
      <span className="num text-brand" style={{ fontSize: 11 }}>
        {file}
      </span>
      <span className="faint" style={{ fontSize: 11.5 }}>
        {desc}
      </span>
    </Stack>
  );
}
